// pages/JobManagementPage.jsx
import { useEffect, useState, useCallback } from "react";
import Pagination from "../components/common/Pagination";
import { jobService } from "../routes/services/AutoGenerateImage/jobService";
import JobRow from "../components/JobRow";

export default function JobManagementPage({ refreshSignal = 0 }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const {
        jobs: fetchedJobs,
        pageNumber: fetchedPageNumber,
        pageSize: fetchedPageSize,
        totalCount: fetchedTotalCount
      } = await jobService.getJobs(pageNumber, pageSize);

      const normalizedPageNumber = Math.max(1, fetchedPageNumber ?? pageNumber);
      const normalizedPageSize = Math.max(1, fetchedPageSize ?? pageSize);
      const normalizedTotalCount =
        typeof fetchedTotalCount === "number"
          ? Math.max(0, fetchedTotalCount)
          : fetchedJobs.length;

      const computedTotalPages = Math.max(
        1,
        Math.ceil(normalizedTotalCount / normalizedPageSize)
      );

      setTotalPages(computedTotalPages);
      setTotalCount(normalizedTotalCount);

      if (normalizedPageNumber > computedTotalPages) {
        setPageNumber(computedTotalPages);
        return;
      }

      setJobs(fetchedJobs);
    } catch (error) {
      console.error("Failed to load jobs", error);
      setJobs([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize]);

  useEffect(() => {
    loadJobs();
    const timer = setInterval(loadJobs, 5000);
    return () => clearInterval(timer);
  }, [loadJobs]);

  useEffect(() => {
    if (refreshSignal <= 0) {
      return;
    }

    loadJobs();
  }, [refreshSignal, loadJobs]);

  const handlePageChange = (nextPage) => {
    const boundedPage = Math.max(1, Math.min(totalPages, nextPage));
    setPageNumber(boundedPage);
  };

  const firstItem = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const lastItem = Math.min(totalCount, pageNumber * pageSize);

  return (
    <div className="container py-4">
      <h4>Background Job Manager</h4>
      <table className="table table-sm table-hover align-middle">
        <thead className="table-light">
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Status</th>
            <th style={{ width: 200 }}>Progress</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} onReload={loadJobs} />
          ))}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <small className="text-muted">
          Showing {firstItem === 0 ? 0 : firstItem}-{lastItem} of {totalCount}{" "}
          job{totalCount === 1 ? "" : "s"}
        </small>

        <Pagination
          currentPage={pageNumber}
          totalPages={totalPages}
          onChange={handlePageChange}
        />
      </div>
    </div >
  );
}
